import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "../services/api";
import { useSocketStore } from "./socket";

export const useVotesStore = defineStore("votes", () => {
  const votes = ref({});
  const userVotes = ref({});
  const upNextSongIds = ref({});
  const queueEntries = ref({});
  const nowPlaying = ref({});
  const performedHistory = ref({});
  const isLoading = ref(false);
  const currentEventId = ref(null);

  const getEventVotes = computed(() => (eventId) => {
    return votes.value[eventId] || [];
  });

  const getQueueEntries = computed(() => (eventId) => {
    return queueEntries.value[eventId] || [];
  });

  const getNowPlaying = computed(() => (eventId) => {
    return nowPlaying.value[eventId] || null;
  });

  const getPerformedHistory = computed(() => (eventId) => {
    return performedHistory.value[eventId] || [];
  });

  const getUserVotesForEvent = computed(() => (eventId) => {
    return userVotes.value[eventId] || [];
  });

  const getSongVoteCount = computed(() => (eventId, songId) => {
    const eventVotes = votes.value[eventId] || [];
    const songVote = eventVotes.find((vote) => vote.songId === songId || vote.song?.id === songId);
    return songVote ? songVote.totalVotes : 0;
  });

  const hasUserVotedForSong = computed(() => (eventId, songId) => {
    const userEventVotes = userVotes.value[eventId] || [];
    return userEventVotes.some((vote) => vote.songId === songId);
  });

  const getUpNextSongId = computed(() => (eventId) => {
    return upNextSongIds.value[eventId] || null;
  });

  const applyEventVotePayload = (eventId, data) => {
    if (Array.isArray(data.queueEntries)) {
      queueEntries.value[eventId] = data.queueEntries;
      nowPlaying.value[eventId] = data.nowPlaying || null;
      performedHistory.value[eventId] = data.performedHistory || [];
      votes.value[eventId] = [];
      upNextSongIds.value[eventId] = null;
      return;
    }

    votes.value[eventId] = data.results || [];
    upNextSongIds.value[eventId] = data.upNextSongId || null;
    queueEntries.value[eventId] = [];
    nowPlaying.value[eventId] = null;
    performedHistory.value[eventId] = [];
  };

  const submitVote = async (eventId, songId, options = {}) => {
    try {
      isLoading.value = true;

      const payload = { eventId, songId };
      if (options.performerName) {
        payload.performerName = options.performerName;
      }

      const response = await api.post("/api/votes", payload);

      if (response.data.success) {
        await fetchEventVotes(eventId);
        await fetchUserVotes(eventId);

        const socketStore = useSocketStore();
        socketStore.emit("submit_vote", { eventId, songId });

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to submit vote"
      };
    } finally {
      isLoading.value = false;
    }
  };

  const fetchEventVotes = async (eventId) => {
    try {
      const response = await api.get(`/api/votes/event/${eventId}`);

      if (response.data.success) {
        applyEventVotePayload(eventId, response.data.data);
        currentEventId.value = eventId;
      }
    } catch (error) {
      console.error("Failed to fetch event votes:", error);
    }
  };

  const fetchUserVotes = async (eventId) => {
    try {
      const deviceId = localStorage.getItem("deviceId");
      if (deviceId) {
        const response = await api.get(`/api/votes/device/${eventId}`);
        if (response.data.success) {
          userVotes.value[eventId] = response.data.data.votes || [];
        }
      } else {
        userVotes.value[eventId] = [];
      }
    } catch (error) {
      console.error("Failed to fetch user votes:", error);
      userVotes.value[eventId] = [];
    }
  };

  const markAsPerformed = async (eventId, songId, options = {}) => {
    try {
      const payload = {
        eventId,
        songId,
        skipAutoQueue: options.skipAutoQueue || false
      };
      if (options.voteId) {
        payload.voteId = options.voteId;
      }

      const response = await api.post("/api/votes/performed", payload);

      if (response.data.success) {
        await fetchEventVotes(eventId);

        const socketStore = useSocketStore();
        socketStore.emit("mark_performed", { eventId, songId });

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to mark song as performed"
      };
    }
  };

  const setEndTime = async (eventId, songId, options = {}) => {
    try {
      const payload = { eventId, songId };
      if (options.performedSongId) {
        payload.performedSongId = options.performedSongId;
      }

      const response = await api.post("/api/votes/performed/end", payload);

      if (response.data.success) {
        await fetchEventVotes(eventId);

        const socketStore = useSocketStore();
        socketStore.emit("song_ended", { eventId, songId });

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to set song end time"
      };
    }
  };

  const clearUpNext = async (eventId) => {
    try {
      const response = await api.post("/api/votes/clear-up-next", { eventId });

      if (response.data.success) {
        upNextSongIds.value[eventId] = null;

        const socketStore = useSocketStore();
        socketStore.emit("song_performed", { eventId, upNextSongId: null });

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to unlock up next song"
      };
    }
  };

  const startNextSet = async (eventId, extraDuration = undefined) => {
    try {
      const payload = { eventId };
      if (extraDuration !== undefined) payload.extraDuration = extraDuration;
      const response = await api.post("/api/votes/start-next-set", payload);
      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data.message || "Failed to start next set" };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to start next set"
      };
    }
  };

  const handleVoteUpdated = async (data) => {
    const { eventId, results } = data;

    if (eventId && results && Array.isArray(results)) {
      votes.value[eventId] = results;
      console.log(`✅ Vote results updated from socket for event ${eventId}`);
    } else if (eventId) {
      await fetchEventVotes(eventId);
    }
  };

  const handleSongPerformed = async (data) => {
    const { eventId, upNextSongId } = data;

    if (eventId && upNextSongId !== undefined) {
      upNextSongIds.value[eventId] = upNextSongId || null;
    }

    if (eventId) {
      await fetchEventVotes(eventId);
    }
  };

  const handleSongEnded = async (data) => {
    const { eventId } = data;

    if (eventId) {
      await fetchEventVotes(eventId);
    }
  };

  const clearVotes = () => {
    votes.value = {};
    userVotes.value = {};
    queueEntries.value = {};
    nowPlaying.value = {};
    performedHistory.value = {};
    currentEventId.value = null;
  };

  return {
    votes,
    userVotes,
    upNextSongIds,
    queueEntries,
    nowPlaying,
    performedHistory,
    isLoading,
    currentEventId,
    getEventVotes,
    getQueueEntries,
    getNowPlaying,
    getPerformedHistory,
    getUserVotesForEvent,
    getSongVoteCount,
    hasUserVotedForSong,
    getUpNextSongId,
    submitVote,
    fetchEventVotes,
    fetchUserVotes,
    markAsPerformed,
    setEndTime,
    clearUpNext,
    startNextSet,
    handleVoteUpdated,
    handleSongPerformed,
    handleSongEnded,
    clearVotes
  };
});
